<template>
  <div>
    <!--Toolbar-->
    <div class="d-flex flex-wrap align-items-center mb-1" style="gap: 8px">
      <b-input-group size="sm" style="max-width: 320px">
        <b-input-group-prepend is-text><i class="fa-solid fa-magnifying-glass"></i></b-input-group-prepend>
        <b-form-input v-model="search" placeholder="Name, acronym, domain, ID..." debounce="200"/>
      </b-input-group>
      <b-form-select v-model="deskFilter" :options="deskOptions" size="sm" style="max-width: 180px"/>
      <b-form-checkbox v-model="groupByDesk" switch class="ml-50">Group by desk</b-form-checkbox>
      <span class="ml-auto text-muted small">{{ filteredRows.length }} / {{ rows.length }}</span>
      <b-button size="sm" variant="outline-secondary" @click="refresh" :disabled="loading">
        <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
      </b-button>
    </div>

    <div v-if="loading && rows.length === 0" class="text-center py-5">
      <b-spinner variant="primary"/>
    </div>
    <div v-else-if="filteredRows.length === 0" class="text-center text-muted py-5">
      No organizations found
    </div>

    <!--Groups-->
    <div v-for="group in groups" :key="group.name" class="mb-2">
      <h6 v-if="groupByDesk" class="text-muted text-uppercase mb-1">
        {{ group.name }} <b-badge variant="light-secondary" pill>{{ group.items.length }}</b-badge>
      </h6>
      <div class="org-grid">
        <b-card v-for="org in group.items" :key="org.id" no-body class="org-card mb-0"
                :style="{ borderTopColor: org.color || '#ced4da' }" @click="$emit('selected', org.id)">
          <b-card-body class="p-1 d-flex flex-column">
            <!--Header-->
            <div class="d-flex align-items-center mb-1">
              <div class="org-avatar" :style="avatarStyle(org.color)">{{ shortAcronym(org) }}</div>
              <div class="ml-1 overflow-hidden flex-grow-1">
                <div class="font-weight-bold text-truncate" :title="org.name">{{ org.name }}</div>
                <div class="small text-muted text-truncate">
                  {{ org.desk || '—' }}<span v-if="org.country"> · {{ org.country.toUpperCase() }}</span>
                </div>
              </div>
              <b-button size="sm" variant="flat-secondary" class="p-25" v-b-tooltip.hover title="Copy ID"
                        @click.stop="copyId(org.id)">
                <i class="fa-regular fa-copy"></i>
              </b-button>
            </div>

            <!--Links-->
            <div class="small mb-1">
              <a v-if="org.website" :href="toUrl(org.website)" target="_blank" rel="noopener"
                 class="d-block text-truncate" @click.stop>
                <i class="fa-solid fa-globe fa-fw text-muted"></i> {{ displayHost(org.website) }}
              </a>
              <a v-if="org.vipWebsite" :href="toUrl(org.vipWebsite)" target="_blank" rel="noopener"
                 class="d-block text-truncate" @click.stop>
                <i class="fa-solid fa-crown fa-fw text-warning"></i> {{ displayHost(org.vipWebsite) }}
              </a>
              <a v-if="org.crm" :href="toUrl(org.crm)" target="_blank" rel="noopener"
                 class="d-block text-truncate" @click.stop>
                <i class="fa-solid fa-briefcase fa-fw text-muted"></i> {{ displayHost(org.crm) }}
              </a>
            </div>

            <!--Settings badges-->
            <div class="mb-1">
              <b-badge variant="light-primary" class="mr-25" v-b-tooltip.hover title="Language">
                {{ (org.lang || '—').toUpperCase() }}
              </b-badge>
              <b-badge variant="light-info" class="mr-25" v-b-tooltip.hover title="Currency / default trade currency">
                <template  v-if="org.defaultTradeCurrency && org.defaultTradeCurrency !== org.currency">
                {{ org.defaultTradeCurrency || '—' }}<span>
                  → {{ org.currency }}</span>
                </template>
                <template v-else>
                  {{ org.currency || '—' }}
                </template>
              </b-badge>
              <b-badge v-for="p in processorSummary(org)" :key="p.key" class="mr-25"
                       :variant="p.testMode ? 'light-warning' : (p.active ? 'light-success' : 'light-secondary')"
                       v-b-tooltip.hover :title="p.title">
                {{ p.name }}<span v-if="p.testMode"> (test)</span>
              </b-badge>
            </div>

            <!--Footer-->
            <div class="mt-auto d-flex align-items-center small text-muted pt-50 border-top" style="gap: 12px">
              <span v-b-tooltip.hover :title="(org.hostnames || []).join('\n')">
                <i class="fa-solid fa-server"></i> {{ (org.hostnames || []).length }}
              </span>
              <span v-b-tooltip.hover :title="(org.allowedDomains || []).join('\n')">
                <i class="fa-solid fa-shield-halved"></i> {{ (org.allowedDomains || []).length }}
              </span>
              <i class="fa-brands fa-telegram" :class="org.telegramTransactionsGroup ? 'text-info' : 'org-off'"
                 v-b-tooltip.hover :title="org.telegramTransactionsGroup ? 'Telegram group configured' : 'No Telegram group'"></i>
              <i class="fa-solid fa-heart-pulse" :class="org.websiteHealthCheckUrl ? 'text-success' : 'org-off'"
                 v-b-tooltip.hover :title="org.websiteHealthCheckUrl ? 'Health check configured' : 'No health check'"></i>
              <i v-if="isMissingColor(org)" class="fa-solid fa-palette text-warning ml-auto"
                 v-b-tooltip.hover title="No color set"></i>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import { mapActions } from 'vuex';

const NO_DESK = 'No desk';

export default {
  name: 'OrganizationsGrid',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    rows: [],
    loading: false,
    search: '',
    deskFilter: null,
    groupByDesk: false,
  }),
  created() {
    this.refresh()
  },
  computed: {
    deskOptions() {
      const desks = [...new Set(this.rows.map(o => o.desk || NO_DESK))].sort(this.naturalCompare)
      return [{ value: null, text: 'All desks' }, ...desks.map(d => ({ value: d, text: d }))]
    },
    filteredRows() {
      const q = (this.search || '').trim().toLowerCase()
      return this.rows
        .filter(o => this.deskFilter == null || (o.desk || NO_DESK) === this.deskFilter)
        .filter(o => !q || [
          o.id, o.name, o.acronym, o.desk, o.country, o.website, o.vipWebsite, o.crm,
          ...(o.hostnames || []), ...(o.allowedDomains || []),
        ].some(v => v && String(v).toLowerCase().includes(q)))
        .sort((a, b) => this.naturalCompare(a.name || '', b.name || ''))
    },
    groups() {
      if (!this.groupByDesk) {
        return [{ name: 'all', items: this.filteredRows }]
      }
      const byDesk = this.filteredRows.reduce((acc, o) => {
        const desk = o.desk || NO_DESK;
        (acc[desk] = acc[desk] || []).push(o)
        return acc
      }, {})
      return Object.keys(byDesk).sort(this.naturalCompare).map(name => ({ name, items: byDesk[name] }))
    },
  },
  methods: {
    ...mapActions('organizations', ['getAllOrganizations']),
    async refresh() {
      this.loading = true
      try {
        this.rows = (await this.getAllOrganizations()) || []
      } catch (err) {
        this.showErrorNotification(this.$t('globalMessages.fetchDataError', { system: 'organizations' }), this.getErrorDescription(err));
      } finally {
        this.loading = false
      }
    },
    naturalCompare(a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
    },
    shortAcronym(org) {
      const acronym = org.acronym || org.name || '?'
      return acronym.length > 3 ? acronym.substring(0, 3).toUpperCase() : acronym.toUpperCase()
    },
    isMissingColor(org) {
      return !/^#[0-9a-f]{6}$/i.test(org.color || '')
    },
    avatarStyle(color) {
      if (this.isMissingColor({ color })) {
        return { backgroundColor: '#e9ecef', color: '#6e6b7b' }
      }
      const r = parseInt(color.substr(1, 2), 16), g = parseInt(color.substr(3, 2), 16), b = parseInt(color.substr(5, 2), 16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return { backgroundColor: color, color: luminance > 0.6 ? '#222' : '#fff' }
    },
    toUrl(value) {
      const v = String(value).trim()
      return /^https?:\/\//i.test(v) ? v : `https://${v}`
    },
    displayHost(value) {
      return String(value).trim().replace(/^https?:\/\//i, '').replace(/\/$/, '')
    },
    // Only names and flags are shown: processor params contain API keys and must never be rendered here
    processorSummary(org) {
      return (org.paymentProcessors || []).map((p, i) => ({
        key: `${p.processorName}-${i}`,
        name: p.processorName,
        active: p.active === true,
        testMode: p.testMode === true,
        title: `${p.processorName} · ${p.active ? 'active' : 'inactive'}${p.testMode ? ' · test mode' : ''}`,
      }))
    },
    copyId(id) {
      navigator.clipboard.writeText(id).then(() => {
        this.showSuccessNotification(this.$t('globalMessages.copySuccess'))
      })
    },
  }
};
</script>

<style scoped>
.org-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.org-card {
  border-top: 4px solid;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
}

.org-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 18px rgba(34, 41, 47, .15);
}

.org-avatar {
  flex: 0 0 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
}

.org-off {
  opacity: .3;
}
</style>
